<template>
  <div class="rte-container">
    <label v-if="label" class="rte-label" :for="inputId">{{ label }}<vue-feather v-if="tooltip" type="info" size="14" class="label-tooltip-icon" v-tooltip="tooltip" /></label>
    <div class="rte-wrapper" :class="wrapperClass">
      <div v-if="!disabled" class="rte-toolbar">
        <button type="button" class="rte-tool" title="Bold" @mousedown.prevent @click="exec('bold')">
          <vue-feather type="bold" size="15" />
        </button>
        <button type="button" class="rte-tool" title="Italic" @mousedown.prevent @click="exec('italic')">
          <vue-feather type="italic" size="15" />
        </button>
        <button v-if="underline" type="button" class="rte-tool" title="Underline" @mousedown.prevent @click="exec('underline')">
          <vue-feather type="underline" size="15" />
        </button>
        <div class="rte-tool-divider"></div>
        <button type="button" class="rte-tool" title="Bullet list" @mousedown.prevent @click="exec('insertUnorderedList')">
          <vue-feather type="list" size="15" />
        </button>
        <button type="button" class="rte-tool rte-tool--text" title="Numbered list" @mousedown.prevent @click="exec('insertOrderedList')">1.</button>
      </div>
      <div :id="inputId" ref="editable" class="rte-editable" :contenteditable="!disabled ? 'true' : 'false'"
        :data-placeholder="placeholder" @input="handleInput" @paste="handlePaste" @keydown="handleKeydown"
        @focus="isFocused = true" @blur="handleBlur"></div>
    </div>
    <span v-if="error" class="rte-error">{{ error }}</span>
  </div>
</template>

<script>
import VueFeather from 'vue-feather'
import DOMPurify from 'dompurify'

let inputId = 0

const ALLOWED_TAGS = ['b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'div', 'p', 'br']
// <ol start> keeps a numbered list's numbers when a blank line splits it ("1. a" ⏎⏎ "2. b")
const ALLOWED_ATTR = ['start']

export default {
  name: 'RichTextInput',
  components: {
    'vue-feather': VueFeather
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    tooltip: {
      type: String,
      default: ''
    },
    // false drops underline entirely: no tool, no Ctrl/Cmd+U, never written — and a literal <u> in the value
    // reads as text. For content headed somewhere underline can't go (session notes → the EHR).
    underline: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      inputId: `rte-${++inputId}`,
      isFocused: false,
      lastEmittedMarkdown: null
    }
  },
  computed: {
    wrapperClass() {
      return {
        'rte-wrapper--focused': this.isFocused,
        'rte-wrapper--disabled': this.disabled,
        'rte-wrapper--error': this.error
      }
    },
    allowedTags() {
      return this.underline ? ALLOWED_TAGS : ALLOWED_TAGS.filter(tag => tag !== 'u')
    }
  },
  mounted() {
    this.renderMarkdown(this.modelValue)
  },
  watch: {
    modelValue(value) {
      // Echo guard: re-rendering our own emit would reset the caret mid-typing
      if (value !== this.lastEmittedMarkdown) {
        this.renderMarkdown(value)
      }
    }
  },
  methods: {
    exec(command) {
      this.$refs.editable.focus()
      document.execCommand(command, false, null)
      this.handleInput()
    },

    handleInput() {
      const markdown = this.htmlToMarkdown(this.$refs.editable.innerHTML)
      this.lastEmittedMarkdown = markdown
      this.$emit('update:modelValue', markdown)
    },

    handleBlur() {
      this.isFocused = false
      this.$emit('blur')
    },

    handleKeydown(event) {
      // The browser's own Ctrl/Cmd+U would underline text that is never saved
      if (!this.underline && (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'u') {
        event.preventDefault()
      }
    },

    handlePaste(event) {
      event.preventDefault()
      const html = event.clipboardData.getData('text/html')
      if (html) {
        const clean = DOMPurify.sanitize(html, { ALLOWED_TAGS: this.allowedTags, ALLOWED_ATTR: [] })
        document.execCommand('insertHTML', false, clean)
      } else {
        const text = event.clipboardData.getData('text/plain')
        if (text) document.execCommand('insertText', false, text)
      }
      this.handleInput()
    },

    renderMarkdown(markdown) {
      const editable = this.$refs.editable
      if (!editable) return
      editable.innerHTML = this.markdownToHtml(markdown || '')
      this.lastEmittedMarkdown = markdown || ''
    },

    markdownToHtml(markdown) {
      const lines = String(markdown).split('\n')
      const blocks = []
      let listTag = null

      const closeList = () => {
        if (listTag) {
          blocks.push(`</${listTag}>`)
          listTag = null
        }
      }
      // A numbered list starts at its first item's number, so one split by a blank line keeps counting
      const openList = (tag, start = 1) => {
        if (listTag !== tag) {
          closeList()
          blocks.push(start !== 1 ? `<${tag} start="${start}">` : `<${tag}>`)
          listTag = tag
        }
      }

      for (const line of lines) {
        const bulletMatch = line.match(/^\s*[-*]\s+(.*)$/)
        const numberedMatch = line.match(/^\s*(\d+)\.\s+(.*)$/)

        if (bulletMatch) {
          openList('ul')
          blocks.push(`<li>${this.inlineMarkdownToHtml(bulletMatch[1])}</li>`)
        } else if (numberedMatch) {
          openList('ol', Number(numberedMatch[1]))
          blocks.push(`<li>${this.inlineMarkdownToHtml(numberedMatch[2])}</li>`)
        } else {
          closeList()
          const trimmed = line.trim()
          if (trimmed) {
            blocks.push(`<div>${this.inlineMarkdownToHtml(trimmed)}</div>`)
          } else {
            blocks.push('<div><br></div>')
          }
        }
      }
      closeList()

      // Drop a single trailing blank block so re-renders don't grow the editor
      if (blocks.length > 1 && blocks[blocks.length - 1] === '<div><br></div>') {
        blocks.pop()
      }

      return DOMPurify.sanitize(blocks.join(''), { ALLOWED_TAGS: this.allowedTags, ALLOWED_ATTR })
    },

    inlineMarkdownToHtml(text) {
      let escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      // Underline has no markdown syntax - <u> tags pass through the escape (when underline is offered)
      if (this.underline) {
        escaped = escaped
          .replace(/&lt;u&gt;/g, '<u>')
          .replace(/&lt;\/u&gt;/g, '</u>')
      }
      return escaped
        .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
        .replace(/\*(.+?)\*/g, '<i>$1</i>')
    },

    htmlToMarkdown(html) {
      const doc = new DOMParser().parseFromString(
        DOMPurify.sanitize(html, { ALLOWED_TAGS: this.allowedTags, ALLOWED_ATTR }),
        'text/html'
      )
      const markdown = this.blockToMarkdown(doc.body)
      return markdown.replace(/\n{3,}/g, '\n\n').replace(/\s+$/, '')
    },

    blockToMarkdown(node) {
      const lines = []
      let pendingInline = ''

      const flushInline = () => {
        lines.push(pendingInline)
        pendingInline = ''
      }

      for (const child of node.childNodes) {
        const tag = child.nodeType === Node.ELEMENT_NODE ? child.tagName.toLowerCase() : null

        if (tag === 'div' || tag === 'p') {
          if (pendingInline) flushInline()
          lines.push(this.blockToMarkdown(child))
        } else if (tag === 'ul' || tag === 'ol') {
          if (pendingInline) flushInline()
          let index = tag === 'ol' ? (parseInt(child.getAttribute('start'), 10) || 1) : 1
          for (const li of child.children) {
            const prefix = tag === 'ul' ? '- ' : `${index}. `
            lines.push(prefix + this.inlineToMarkdown(li))
            index++
          }
        } else if (tag === 'br') {
          flushInline()
        } else {
          pendingInline += this.inlineToMarkdown(child)
        }
      }
      if (pendingInline) flushInline()

      return lines.join('\n')
    },

    inlineToMarkdown(node) {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent
      }
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return ''
      }

      let inner = ''
      for (const child of node.childNodes) {
        inner += this.inlineToMarkdown(child)
      }

      const tag = node.tagName.toLowerCase()
      if (!inner.trim()) return inner
      if (tag === 'b' || tag === 'strong') return `**${inner}**`
      if (tag === 'i' || tag === 'em') return `*${inner}*`
      if (tag === 'u') return this.underline ? `<u>${inner}</u>` : inner
      if (tag === 'br') return '\n'
      return inner
    },

    focus() {
      this.$refs.editable.focus()
    }
  },
  emits: ['update:modelValue', 'blur']
}
</script>

<style scoped>
.rte-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.rte-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 4px;
}

.label-tooltip-icon {
  color: #9ca3af;
  cursor: help;
  flex-shrink: 0;
}

.rte-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  background: #FFF;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-sizing: border-box;
  transition: all 0.15s ease;
}

.rte-wrapper:hover:not(.rte-wrapper--disabled):not(.rte-wrapper--error) {
  border-color: #9ca3af;
}

.rte-wrapper--focused:not(.rte-wrapper--error) {
  border-color: var(--color-primary, #6a16f0);
  box-shadow: 0 0 0 3px rgba(106, 22, 240, 0.1);
}

.rte-wrapper--disabled {
  background: #fafafa;
}

.rte-wrapper--error {
  border-color: #dc2626;
}

.rte-wrapper--error.rte-wrapper--focused {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

.rte-toolbar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  border-bottom: 1px solid #e5e7eb;
}

.rte-tool {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;
}

.rte-tool:hover {
  background: #f3f4f6;
  color: #374151;
}

.rte-tool--text {
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
}

.rte-tool-divider {
  width: 1px;
  height: 18px;
  background: #e5e7eb;
  margin: 0 4px;
}

.rte-editable {
  width: 100%;
  min-height: 108px;
  padding: 12px 14px 10px;
  font-size: 1rem;
  font-weight: 400;
  font-family: inherit;
  line-height: 21px;
  color: #374151;
  outline: none;
  box-sizing: border-box;
  overflow-wrap: break-word;
  white-space: pre-wrap;
}

.rte-editable:empty::before {
  content: attr(data-placeholder);
  color: #9ca3af;
  pointer-events: none;
}

.rte-editable[contenteditable='false'] {
  cursor: default;
}

.rte-editable :deep(ul),
.rte-editable :deep(ol) {
  margin: 0;
  padding-left: 22px;
}

.rte-error {
  font-size: 0.8125rem;
  color: #dc2626;
}
</style>
